import React, { useEffect, useMemo, useState } from "react";
import { ActionButton } from "../components/action-button";
import "./email-prefs.scss";
import { Nest } from "../components/block-content/nest";
import classNames from "classnames";
import {
  getMailBluster,
  isChromeLatest,
  subscribeMailBluster,
  unsubscribeMailBluster,
} from "../utils/utils";
import { MailBlusterLead } from "../globals";

type List = {
  name: string;
  id: string;
  enabled: boolean;
};
type EmailState = {
  products: List[];
  subLists: List[];
  extraTags: string[];
};

const defaultEmailState: EmailState = {
  products: [
    {
      name: "Version Raptor",
      id: "product-version-raptor",
      enabled: true,
    },
    {
      name: "Webcam",
      id: "product-webcam",
      enabled: true,
    },
    {
      name: "Klutz GPT",
      id: "product-klutz-gpt",
      enabled: true,
    },
    {
      name: "Pro IO",
      id: "product-pro-io",
      enabled: true,
    },
    {
      name: "Webcam for Express",
      id: "product-webcam-for-express",
      enabled: true,
    },
  ],
  subLists: [
    {
      name: "for Creative Leads",
      id: "sublist-creative-leads",
      enabled: true,
    },
    {
      name: "for Artists",
      id: "sublist-artists",
      enabled: true,
    },
    {
      name: "for Developers",
      id: "sublist-developers",
      enabled: true,
    },
  ],
  extraTags: [],
};

export const EmailPrefs = (props: { newEmail: boolean }) => {
  const { newEmail } = props;
  const [lead, setLead] = useState<MailBlusterLead | null>(null);
  const [state, setState] = useState<EmailState>(defaultEmailState);
  const [email, setEmail] = useState("");
  const [completed, setCompleted] = useState(false);
  const [status, setStatus] = useState<"init" | "fetching" | "result">("init");
  const [mode, setMode] = useState<"new" | "update">(
    newEmail ? "new" : "update",
  );
  const emailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
    [email],
  );

  const stateToLead = (unsubscribeAll: boolean) => {
    if (lead) {
      if (unsubscribeAll) {
        lead.subscribed = false;
        return lead;
      } else {
        state.products.map((product) => {
          const enabled = product.enabled;
          if (enabled && !lead.tags.includes(product.id)) {
            lead.tags.push(product.id);
          } else if (!enabled && lead.tags.includes(product.id)) {
            lead.tags = lead.tags.filter((tag) => tag !== product.id);
          }
        });
        state.subLists.map((subList) => {
          const enabled = subList.enabled;
          if (enabled && !lead.tags.includes(subList.id)) {
            lead.tags.push(subList.id);
          } else if (!enabled && lead.tags.includes(subList.id)) {
            lead.tags = lead.tags.filter((tag) => tag !== subList.id);
          }
        });
        if (state.extraTags) {
          state.extraTags.map((tag) => lead.tags.push(tag));
        }
      }
      return lead;
    }
  };

  const getTags = () => {
    let tags: string[] = [];
    state.products.map((product) => {
      if (product.enabled) tags.push(product.id);
    });
    state.subLists.map((subList) => {
      if (subList.enabled) tags.push(subList.id);
    });
    state.extraTags.map((extraTag) => {
      tags.push(extraTag);
    });
    return tags;
  };

  const leadToState = (lead: MailBlusterLead) => {
    const newState = state;
    if (!lead.subscribed) {
      newState.products = state.products.map((p) => ({ ...p, enabled: false }));
      newState.subLists = state.subLists.map((p) => ({ ...p, enabled: false }));
    } else {
      newState.products = state.products.map((p) => {
        return { ...p, enabled: lead.tags.includes(p.id) };
      });
      newState.subLists = state.subLists.map((p) => {
        return { ...p, enabled: lead.tags.includes(p.id) };
      });
    }
    setState({ ...newState });
  };

  const getUrlParam = (id: string) => {
    if (globalThis?.window?.location?.href) {
      const url = new URL(globalThis?.window?.location?.href);
      const e = url.searchParams.get(id);
      return e;
    }
    return null;
  };

  const getEmailPrefs = async (email: string) => {
    console.log("getEmailPrefs");
    setStatus("fetching");
    const e = getUrlParam("email");
    if (e) {
      console.log({ e });
      setEmail(e);
      try {
        const res = await getMailBluster(e, ["website"]);
        console.log({ res });
        if (res.success || res.data) {
          const data = res.data;
          setLead(data);
          setStatus("result");
          return leadToState(data);
        } else {
          console.error("No Email Found");
          setEmail("");
        }
      } catch (e) {
        console.error("No Email Found");
        setEmail("");
      }
      setEmail("");
      setStatus("result");
    } else {
      return console.error("no email found");
    }
  };

  const toggleLabel = (id: string, value: boolean) => {
    console.log({ id, value });
    state.products = state.products.map((product) => {
      if (product.id === id) {
        console.log(`Found, Updatefrom ${product.enabled} to ${value}`);
        return { ...product, enabled: value };
      } else {
        return { ...product };
      }
    });
    state.subLists = state.subLists.map((sublist) => {
      if (sublist.id === id) {
        console.log(`Found, Updatefrom ${sublist.enabled} to ${value}`);
        return { ...sublist, enabled: value };
      } else {
        return { ...sublist };
      }
    });
    setState({ ...state });
  };

  const join = () => {
    const tags = getTags();
    setCompleted(true);
    subscribeMailBluster(email, tags);
  };
  const update = () => {
    const lead = stateToLead(false);
    if (lead) {
      console.log("update lead", lead);
      setCompleted(true);
      subscribeMailBluster(email, lead.tags);
    } else {
      console.error("No lead found");
    }
  };
  const unsubscribeAll = () => {
    const lead = stateToLead(true);
    if (lead) {
      console.log("unsub lead", lead);
      setCompleted(true);
      unsubscribeMailBluster(email, lead.tags);
    } else {
      console.error("No lead found");
    }
  };

  const getHiddenTags = () => {
    const tagsStr = getUrlParam("tags");
    if (tagsStr) {
      console.log({ tagsStr });
      const tags = tagsStr.split(",");
      console.log({ tags });
      const productTags = tags.filter((tag) => tag.startsWith("product-"));
      const sublistTags = tags.filter((tag) => tag.startsWith("sublist-"));
      const extraTags = tags.filter(
        (tag) => !tag.startsWith("sublist-") && !tag.startsWith("product-"),
      );
      if (productTags.length > 0) {
        // Disable all other products
        state.products = state.products.map((product) => {
          return {
            ...product,
            enabled: productTags.includes(product.id),
          };
        });
      }
      if (sublistTags.length > 0) {
        // Disable all other sublists
        state.subLists = state.subLists.map((subList) => {
          return {
            ...subList,
            enabled: sublistTags.includes(subList.id),
          };
        });
      }
      if (extraTags) {
        state.extraTags = extraTags;
      }
      setState({ ...state });
    }
  };

  useEffect(() => {
    console.log("find email info");
    if (newEmail) {
      getHiddenTags();
      setStatus("result");
    } else {
      getEmailPrefs(email);
    }
  }, []);

  return (
    <div
      className={classNames("email-prefs-wrapper", {
        // "calculator-form--meta--wrapper-chrome": isChromeLatest(),
      })}
    >
      {(status === "result" &&
        (newEmail || email) &&
        ((completed && (
          <label>
            {(newEmail &&
              `You have subscribed to the newsletter for: ${email}`) ||
              `Your Email Preferences have been updated for: ${email}`}
          </label>
        )) || (
          <>
            {(newEmail && (
              <input
                name="email"
                className="small-text-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="text"
                placeholder="astro@example.com"
              />
            )) || <label htmlFor="">For {email}</label>}
            {!newEmail && (
              <div className="email-prefs-groups">
                <div>
                  {/* <input
                type="checkbox"
                checked={state.mailingList}
                onChange={(e) => {
                  e.preventDefault();
                  toggleSublists();
                }}
              /> */}
                  <label>Mailing List</label>
                </div>
                {/* Don't show sublists for new sign ups */}
                <div className="email-prefs-sub-checkboxes">
                  {state.subLists.map((subList, i) => {
                    return (
                      <div className="email-prefs-groups" key={i}>
                        <input
                          type="checkbox"
                          checked={subList.enabled}
                          onChange={(e) => {
                            toggleLabel(subList.id, !subList.enabled);
                          }}
                        />
                        <label>{subList.name}</label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="email-prefs-groups">
              <div>
                {/* <input
                type="checkbox"
                checked={state.productUpdates}
                // checked={joinMailingList}
                // onChange={(e) => setJoinMailingList(e.target.checked)}
              /> */}
                <label>Product Updates</label>
              </div>
              <div className="email-prefs-sub-checkboxes">
                {state.products.map((product, i) => {
                  return (
                    <div className="email-prefs-groups" key={i}>
                      <input
                        type="checkbox"
                        checked={product.enabled}
                        onChange={(e) => {
                          toggleLabel(product.id, !product.enabled);
                        }}
                      />
                      <label>{product.name}</label>
                    </div>
                  );
                })}
              </div>
            </div>
            {(newEmail && (
              <div className="email-prefs-buttons">
                <ActionButton
                  text="Join Newsletter"
                  variant="solid"
                  isDisabled={!emailValid}
                  onClick={join}
                />
              </div>
            )) || (
              <div className="email-prefs-buttons">
                <ActionButton text="Update" variant="solid" onClick={update} />
                <ActionButton
                  text="Unsubscribe All"
                  variant="outline"
                  onClick={unsubscribeAll}
                />
              </div>
            )}
          </>
        ))) ||
        (status === "fetching" && <label htmlFor="">Fetching Info...</label>) ||
        (status === "init" && <label htmlFor="">Starting...</label>) ||
        (status === "result" && !email && (
          <label htmlFor="">No email found, try link again...</label>
        ))}
    </div>
  );
};
